import { useColabNovidades } from "@/hooks/useColabNovidades";
import { useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Inbox, Mail, Users, Settings as SettingsIcon, LogOut, CalendarClock, CalendarDays, FolderKanban, ShieldCheck, Calculator, UsersRound, MessageSquare, Sparkles, MessageCircle, Building2, Gauge } from "lucide-react";
import { MensagensProvider, useMensagens } from "@/context/MensagensContext";
import { Button } from "@/components/ui/button";
import MobileTabBar from "@/components/MobileTabBar";
import GavetaLateral from "@/components/GavetaLateral";
import { GavetaProvider, useGaveta } from "@/context/GavetaContext";
import { ConfirmBaixaHost } from "@/components/ConfirmBaixa";
import ScrollPagina from "@/components/ScrollPagina";
import GlobalSearch from "@/components/GlobalSearch";
import logoMark from "@/assets/logo-mark.png";

// Avatar simples (sem @radix-ui/react-avatar): o componente Avatar/AvatarImage do
// Radix troca o <img> pelo fallback via um efeito interno que, no React 19, entra
// em conflito com a reconciliação do React durante navegação de rota e derruba a
// tela inteira com "NotFoundError: removeChild". Isso evita o bug por completo.
function UserAvatar({ src, name }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = src && !imgFailed;
  return (
    <div className="relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full bg-stone-700">
      {showImage && (
        <img
          src={src}
          alt={name || "Usuário"}
          className="aspect-square h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      )}
      {!showImage && (
        <span className="flex h-full w-full items-center justify-center text-xs text-stone-100">
          {name?.[0] || "?"}
        </span>
      )}
    </div>
  );
}

const nav = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/audiencias", label: "Agenda", icon: CalendarClock, testid: "nav-hearings" },
  { to: "/publicacoes", label: "Publicações", icon: Mail, testid: "nav-publicacoes" },
  { to: "/calendario", label: "Calendário", icon: CalendarDays, testid: "nav-calendario" },
  { to: "/calculadora-prazos", label: "Calculadora de prazos", icon: Calculator, testid: "nav-calculadora-prazos" },
  { to: "/revisao", label: "Para validar", icon: Inbox, testid: "nav-review" },
  { to: "/processos", label: "Processos", icon: FolderKanban, testid: "nav-processos" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/compartilhamentos", label: "Compartilhamentos", icon: UsersRound, testid: "nav-compartilhamentos" },
  { to: "/mensagens", label: "Chat", icon: MessageSquare, testid: "nav-mensagens" },
  { to: "/config", label: "Configurações", icon: SettingsIcon, testid: "nav-settings" },
];

// Item de "Administração" só é acrescentado pra quem tem is_owner=true (hoje, só a
// conta do Robson) — as demais contas nem veem essa entrada no menu. O backend também
// tranca essas rotas por conta própria (ver require_owner em server.py), então esconder
// o item aqui é só conveniência de interface, não a única barreira.
// Lote 156 — "Escritório" aparece só para quem faz parte de um escritório (ou é o administrador da plataforma, que cria o primeiro).
const escritorioNavItem = { to: "/escritorio", label: "Escritório", icon: Building2, testid: "nav-escritorio" };
// Lote 164 — Controladoria: Administradores sempre; outras pessoas só se o administrador indicou (user.escritorio.controladoria).
const controladoriaNavItem = { to: "/controladoria", label: "Controladoria", icon: Gauge, testid: "nav-controladoria" };
const adminNavItem = { to: "/admin", label: "Administração", icon: ShieldCheck, testid: "nav-admin" };

export default function Layout() {
  return (
    <MensagensProvider>
      <GavetaProvider>
        <LayoutInner />
      </GavetaProvider>
    </MensagensProvider>
  );
}

// Lote 152 — dois acessos explícitos: "Assistente IA" e "Equipe ②" (o contador de mensagens é só da Equipe).
function BotoesGaveta({ compacto = false }) {
  const { aberta, aba, alternar } = useGaveta();
  const { unread, convites } = useMensagens();
  const total = unread + convites;
  const base = compacto
    ? "relative h-9 w-9 rounded-md inline-flex items-center justify-center border"
    : "relative h-9 px-3 rounded-md inline-flex items-center gap-2 text-sm font-semibold border";
  const cls = (ativo) => `${base} ${ativo ? "bg-stone-200 border-stone-300 text-stone-900" : compacto ? "bg-stone-800 border-stone-700 text-stone-200" : "bg-white border-stone-300 text-stone-700 hover:bg-stone-100"}`;
  return (
    <>
      <button type="button" data-testid="btn-assistente-ia" aria-label="Assistente IA" aria-expanded={aberta && aba === "ia"} onClick={() => alternar("ia")} className={cls(aberta && aba === "ia")}>
        <Sparkles className="w-4 h-4" strokeWidth={1.75} />{!compacto && "Assistente IA"}
      </button>
      <button type="button" data-testid="btn-equipe" aria-label={total > 0 ? `Conversas: ${total} ${total === 1 ? "novidade" : "novidades"}` : "Conversas"} aria-expanded={aberta && aba === "equipe"} onClick={() => alternar("equipe")} className={cls(aberta && aba === "equipe")}>
        <MessageCircle className="w-4 h-4" strokeWidth={1.75} />{!compacto && "Conversas"}
        {total > 0 && <span data-testid="btn-equipe-contador" className={`${compacto ? "absolute -top-1.5 -right-1.5" : ""} min-w-[18px] h-[18px] px-1 rounded-full bg-[#c8502d] text-white text-[11px] font-bold inline-flex items-center justify-center`}>{total}</span>}
      </button>
    </>
  );
}

function LayoutInner() {
  const { unread, convites } = useMensagens();
  const novidadesColab = useColabNovidades();
  const gaveta = useGaveta();
  const mainRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const extrasEsc = [...((user?.escritorio || user?.is_owner) ? [escritorioNavItem] : []), ...(user?.escritorio?.controladoria ? [controladoriaNavItem] : [])];
  const comEscritorio = extrasEsc.length ? [...nav.slice(0, -1), ...extrasEsc, nav[nav.length - 1]] : nav;
  const navItems = user?.is_owner ? [...comEscritorio, adminNavItem] : comEscritorio;

  return (
    <div className="min-h-screen flex bg-stone-950">
      <aside className="hidden md:flex w-64 shrink-0 bg-stone-950 text-stone-300 flex-col border-r border-stone-900">
        <div className="px-6 py-6 border-b border-stone-800">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={logoMark} alt="" className="w-8 h-8 rounded-md shrink-0" />
            <div>
              <div className="font-display font-bold text-stone-100 text-lg leading-none"><span className="text-gold-400">IA</span>pauta</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-1">Audiências e prazos em dia</div>
            </div>
          </Link>
        </div>

        <div className="px-3 pt-4">
          <GlobalSearch />
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-gold-500/15 text-gold-300"
                    : "text-stone-400 hover:bg-stone-900 hover:text-stone-100"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              {label}
              {to === "/compartilhamentos" && novidadesColab > 0 && (
                <span data-testid="nav-compartilhamentos-badge" aria-label={`${novidadesColab} ${novidadesColab === 1 ? "novidade" : "novidades"} em Compartilhamentos`} className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#c8502d] text-white text-[11px] font-bold inline-flex items-center justify-center">{novidadesColab}</span>
              )}
              {to === "/mensagens" && unread + convites > 0 && (
                <span data-testid="nav-mensagens-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-gold-500 text-stone-950 text-[11px] font-bold inline-flex items-center justify-center">{unread + convites}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-800">
          <div className="flex items-center gap-3 mb-3">
            <UserAvatar src={user?.picture} name={user?.name} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-stone-100 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full bg-transparent border-stone-700 text-stone-400 hover:bg-stone-900 hover:text-stone-100"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} /> Sair
          </Button>
        </div>
      </aside>

      <main
        ref={mainRef}
        className={`flex-1 min-w-0 overflow-auto pb-24 md:pb-0 bg-stone-50 transition-[margin] duration-200 ${gaveta.aberta ? "md:mr-[420px]" : ""}`}
      >
        {/* Lote 152 — botões "Assistente IA" e "Equipe" em todas as telas (computador). */}
        <div className="hidden md:flex sticky top-0 z-20 justify-end items-center gap-2 px-8 lg:px-12 pt-4 pb-1 bg-stone-50/90 backdrop-blur" data-testid="topo-acoes">
          {(user?.escritorio?.nome || user?.identidade?.nome) && (
            <span data-testid="topo-escritorio" title={user?.escritorio ? "Seu escritório" : "Seu nome nos avisos ao cliente"} className="mr-auto inline-flex items-center gap-2 max-w-[24rem] rounded-full border border-stone-200 bg-white pl-1.5 pr-3 h-8 text-xs font-semibold text-stone-700">
              {user?.identidade?.tem_logo && user.identidade.logo_url ? <img src={user.identidade.logo_url} alt="" data-testid="topo-escritorio-logo" className="h-5 w-auto max-w-[56px] object-contain" /> : <span className="w-1.5" />}
              <span className="truncate">{user?.identidade?.nome || user.escritorio.nome}</span>
            </span>
          )}
          <BotoesGaveta />
        </div>
        {/* Lote 40 — reinvestigado o "ícone (balança) sumindo no mobile" (item 3 da fila):
            não era (só) o spinner de carregamento (Bloco 1, já corrigido no Lote 32 com o
            placeholder estático do index.html) — a logo com a balança só existia dentro do
            <aside> acima, que é "hidden md:flex" (nunca renderiza abaixo do breakpoint md).
            Ou seja, no celular o ícone realmente NUNCA aparecia em lugar nenhum da interface,
            não só "atrasado". Cabeçalho fixo só-mobile replicando a mesma marca. */}
        <div
          className="md:hidden sticky top-0 z-30 bg-stone-900 border-b border-stone-800 px-4 pb-3 flex items-center gap-2"
          data-testid="mobile-brand-header"
          // Lote 74: o texto ficava colado/escondido sob a borda superior do celular (barra de
          // status/entalhe). A barra escura agora cobre essa área e o texto desce abaixo dela.
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
        >
          <img src={logoMark} alt="" className="w-7 h-7 rounded-md shrink-0" />
          <span className="font-display font-bold text-stone-100 text-base"><span className="text-gold-400">IA</span>pauta</span>
          {/* Lote 69: a frase saiu do Painel (desktop) e fica só aqui, na barra do celular. */}
          <span className="text-[11px] text-stone-400 truncate" data-testid="mobile-brand-tagline">· {user?.identidade?.nome || user?.escritorio?.nome || "Sua agenda inteligente"}</span>
          <div className="ml-auto flex items-center gap-1.5"><BotoesGaveta compacto /></div>
        </div>
        <Outlet />
      </main>

      <GavetaLateral />
      <ConfirmBaixaHost />
      <ScrollPagina />
      <MobileTabBar />
    </div>
  );
}
